import { MedicalRecordRequest } from "#/api/medical-record-requests.type";
import Card from "#/components/shared/card";
import ThemedSpinner from "#/components/shared/spinner";
import Typography from "#/components/shared/typography";
import {
  MedicalRecordRequestDetailProvider,
  useMedicalRecordRequestDetailContext,
} from "../context/MedicalRecordRequestDetailContext";

import ContactDetailEditModal from "./ContactDetailEditModal";
import ContactDetailsCard from "./ContactDetailsCard";
import { EditContactModal } from "./EditContactModal";
import { EditRequestModal } from "./EditRequestModal";
import MedicalRecordRequestStatusTracker from "./MedicalRecordRequestStatusTracker";
import RecipientAddressCard from "./RecipientAddressCard";
import RecipientDetailsCard from "./RecipientDetailsCard";
import RequestSummaryCard from "./RequestSummaryCard";
import { StartCallButton } from "./StartCallButton";

type MedicalRecordRequestDetailProps = {
  requestId?: string;
  request?: MedicalRecordRequest | null;
  allowEdit?: boolean;
};

function MedicalRecordRequestDetailContent() {
  const { isLoading, isError, errorMessage, resolvedRequest, isEditable } =
    useMedicalRecordRequestDetailContext();
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <ThemedSpinner />
      </div>
    );
  }

  if (isError) {
    return (
      <Card variant="cream">
        <Card.Body>
          <Typography variant="h4" className="text-terracotta">
            Unable to load request
          </Typography>
          <Typography variant="body2" className="text-terracotta/80">
            {errorMessage ?? "Unknown error"}
          </Typography>
        </Card.Body>
      </Card>
    );
  }

  if (!resolvedRequest) {
    return (
      <Card variant="cream">
        <Card.Body>
          <Typography variant="body1" className="text-terracotta">
            Request not found.
          </Typography>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <StartCallButton />
      </div>
      <MedicalRecordRequestStatusTracker request={resolvedRequest} />

      <RecipientDetailsCard />

      <RequestSummaryCard />

      <ContactDetailsCard />

      <RecipientAddressCard />

      {isEditable && <EditContactModal />}

      {isEditable && <EditRequestModal />}

      {isEditable && <ContactDetailEditModal />}
    </div>
  );
}

export function MedicalRecordRequestDetail({
  requestId,
  request,
  allowEdit,
}: MedicalRecordRequestDetailProps) {
  return (
    <MedicalRecordRequestDetailProvider
      requestId={requestId}
      request={request}
      allowEdit={allowEdit}
    >
      <MedicalRecordRequestDetailContent />
    </MedicalRecordRequestDetailProvider>
  );
}

export default MedicalRecordRequestDetail;
